/**
 * Dev-only dummy payloads for Freeform Collection UI testing.
 * Enabled via environment.useFreeformDummyData (dev config only).
 */
import dayjs from 'dayjs';
import type {
  ClusterGraphResponse,
  ClusterReviewItem,
  FreeformCollectionExtractResponse,
  FreeformFieldExtract,
  FreeformFieldKey,
  RelatedResponse,
} from './freeform-collection-dashboard.component';

const fieldExtract = (
  freeForms: [string, number][],
  clusters: [string, number][],
  mappings: [string, string, number][]
): FreeformFieldExtract => ({
  free_form: freeForms.map(([value, count]) => ({ value, count })),
  clustered: clusters.map(([value, count]) => ({ value, count })),
  mapping: mappings.map(([cluster_value, free_form_value, count]) => ({
    cluster_value,
    free_form_value,
    count,
  })),
});

const SAMPLE_FIELDS: Partial<Record<FreeformFieldKey, FreeformFieldExtract>> = {
  Topics: fieldExtract(
    [
      ['Flight-Delay-Compensation-Denied', 42],
      ['Aeroplan-Points-Missing-After-Redemption', 31],
      ['Baggage-Delayed-On-International-Itinerary', 28],
      ['Seat-Upgrade-Not-Honored', 19],
      ['Double-Charge-After-Cancellation', 17],
    ],
    [
      ['Travel Disruption', 120],
      ['Loyalty Program', 88],
      ['Baggage', 64],
      ['Ancillary Services', 41],
    ],
    [
      ['Travel Disruption', 'Flight-Delay-Compensation-Denied', 42],
      ['Loyalty Program', 'Aeroplan-Points-Missing-After-Redemption', 31],
      ['Baggage', 'Baggage-Delayed-On-International-Itinerary', 28],
      ['Ancillary Services', 'Seat-Upgrade-Not-Honored', 19],
    ]
  ),
  RootCause: fieldExtract(
    [
      ['Agent-Error-In-Canceling-Flight-Leg', 24],
      ['System-Did-Not-Sync-Fare-Rules', 18],
      ['Weather-Irregular-Operations', 15],
      ['Partner-Airline-Misconnect', 12],
    ],
    [
      ['Agent Process', 55],
      ['System / Tech', 40],
      ['External Ops', 33],
    ],
    [
      ['Agent Process', 'Agent-Error-In-Canceling-Flight-Leg', 24],
      ['System / Tech', 'System-Did-Not-Sync-Fare-Rules', 18],
      ['External Ops', 'Weather-Irregular-Operations', 15],
    ]
  ),
  CustomerPainPoint: fieldExtract(
    [
      ['Long-Hold-Time-Before-Agent', 36],
      ['No-Callback-As-Promised', 22],
      ['Conflicting-Policy-Answers', 14],
    ],
    [
      ['Wait Experience', 70],
      ['Follow-up Failure', 38],
      ['Policy Clarity', 29],
    ],
    [
      ['Wait Experience', 'Long-Hold-Time-Before-Agent', 36],
      ['Follow-up Failure', 'No-Callback-As-Promised', 22],
    ]
  ),
  Trigger: fieldExtract(
    [
      ['Inbound-After-App-Notification', 27],
      ['Email-Reply-Escalation', 16],
      ['Airport-Kiosk-Redirect', 11],
    ],
    [
      ['Digital Trigger', 48],
      ['Contact Center', 35],
    ],
    [
      ['Digital Trigger', 'Inbound-After-App-Notification', 27],
      ['Contact Center', 'Email-Reply-Escalation', 16],
    ]
  ),
  Outcome: fieldExtract(
    [
      ['Refund-Issued-Partial', 21],
      ['Voucher-Offered', 18],
      ['Case-Escalated-To-Specialty', 13],
      ['Issue-Unresolved-Caller-Hung-Up', 9],
    ],
    [
      ['Compensation', 52],
      ['Escalation', 28],
      ['Unresolved', 17],
    ],
    [
      ['Compensation', 'Refund-Issued-Partial', 21],
      ['Compensation', 'Voucher-Offered', 18],
      ['Escalation', 'Case-Escalated-To-Specialty', 13],
    ]
  ),
  Intent: fieldExtract(
    [
      ['Request-Refund', 40],
      ['Check-Flight-Status', 33],
      ['Update-Booking', 25],
    ],
    [
      ['Refund Intent', 61],
      ['Status Inquiry', 44],
      ['Booking Change', 30],
    ],
    [
      ['Refund Intent', 'Request-Refund', 40],
      ['Status Inquiry', 'Check-Flight-Status', 33],
    ]
  ),
  PrimaryTopic: fieldExtract(
    [
      ['Baggage', 55],
      ['Refunds', 48],
      ['Loyalty', 36],
    ],
    [],
    []
  ),
  Sentiment: fieldExtract(
    [
      ['Negative', 90],
      ['Neutral', 60],
      ['Positive', 25],
    ],
    [],
    []
  ),
  JourneyMoment: fieldExtract(
    [
      ['Post-Travel', 70],
      ['Pre-Travel', 45],
      ['In-Travel', 30],
    ],
    [],
    []
  ),
  CallerType: fieldExtract(
    [
      ['Leisure', 80],
      ['Business', 40],
      ['Elite', 25],
    ],
    [],
    []
  ),
  Resolution: fieldExtract(
    [
      ['Resolved', 95],
      ['Partially Resolved', 40],
      ['Unresolved', 22],
    ],
    [],
    []
  ),
};

export function buildDummyExtract(
  startDate: string,
  endDate: string,
  timeZone: string,
  mode: 'unique' | 'all'
): FreeformCollectionExtractResponse {
  if (mode === 'all') {
    const rows = Array.from({ length: 12 }).map((_, i) => ({
      ConversationId: `CONV-DUMMY-${1000 + i}`,
      ContactId: `CONTACT-${2000 + i}`,
      InitiationTimestamp: dayjs(endDate).subtract(i, 'day').toISOString(),
      Summary: `Dummy conversation summary #${i + 1} about delay compensation and loyalty.`,
      PrimaryTopic: i % 2 === 0 ? 'Baggage' : 'Refunds',
      Intent: i % 2 === 0 ? 'Request-Refund' : 'Check-Flight-Status',
      Sentiment: i % 3 === 0 ? 'Negative' : 'Neutral',
      JourneyMoment: 'Post-Travel',
      CallerType: i % 2 === 0 ? 'Leisure' : 'Business',
      Resolution: i % 4 === 0 ? 'Unresolved' : 'Resolved',
      Topics: 'Flight-Delay-Compensation-Denied',
      TopicsClustered: 'Travel Disruption',
      RootCause: 'Weather-Irregular-Operations',
      RootCauseClustered: 'External Ops',
      CustomerPainPoint: 'Long-Hold-Time-Before-Agent',
      CustomerPainPointClustered: 'Wait Experience',
      Trigger: 'Inbound-After-App-Notification',
      TriggerClustered: 'Digital Trigger',
      Outcome: 'Voucher-Offered',
      OutcomeClustered: 'Compensation',
      IntentClustered: 'Refund Intent',
    }));
    return {
      start_date: startDate,
      end_date: endDate,
      timeZone,
      mode: 'all',
      total_conversations: 1880,
      fields: null,
      rows,
      row_count: rows.length,
      truncated: false,
    };
  }

  return {
    start_date: startDate,
    end_date: endDate,
    timeZone,
    mode: 'unique',
    total_conversations: 1880,
    fields: SAMPLE_FIELDS,
    rows: null,
    row_count: null,
    truncated: false,
  };
}

export function buildDummyRelated(query: string, startDate: string, endDate: string, timeZone: string): RelatedResponse {
  const tokens = query
    .toLowerCase()
    .split(/\W+/)
    .filter((t) => t.length > 3)
    .slice(0, 6);
  const matches = [
    {
      field: 'Topics' as FreeformFieldKey,
      field_label: 'Topics',
      layer: 'free_form',
      value: 'Flight-Delay-Compensation-Denied',
      count: 42,
      score: 0.91,
      matched_tokens: tokens.slice(0, 2),
    },
    {
      field: 'RootCause' as FreeformFieldKey,
      field_label: 'Root Cause',
      layer: 'clustered',
      value: 'External Ops',
      count: 33,
      score: 0.78,
      matched_tokens: tokens.slice(0, 1),
    },
    {
      field: 'Outcome' as FreeformFieldKey,
      field_label: 'Outcome',
      layer: 'free_form',
      value: 'Refund-Issued-Partial',
      count: 21,
      score: 0.72,
      matched_tokens: tokens.slice(1, 3),
    },
    {
      field: 'Intent' as FreeformFieldKey,
      field_label: 'Intent',
      layer: 'free_form',
      value: 'Request-Refund',
      count: 40,
      score: 0.69,
      matched_tokens: tokens.slice(0, 2),
    },
  ];
  return {
    query,
    tokens: tokens.length ? tokens : ['delay', 'compensation', 'refund'],
    matches,
    by_field: {
      Topics: [matches[0]],
      RootCause: [matches[1]],
      Outcome: [matches[2]],
      Intent: [matches[3]],
    },
    charts: {
      packedbubble: [
        {
          name: 'Related',
          field: 'Topics',
          data: matches.map((m) => ({
            name: m.value,
            value: m.count,
            color: '#534ad8',
          })),
        },
      ],
      wordcloud: matches.map((m) => ({
        name: m.value,
        weight: m.count,
        field: m.field,
      })),
    },
  };
}

export function buildDummyClusterGraph(
  startDate: string,
  endDate: string,
  timeZone: string
): ClusterGraphResponse {
  const nodes = [
    {
      id: 'c-travel',
      name: 'Travel Disruption',
      type: 'cluster' as const,
      field: 'Topics' as FreeformFieldKey,
      field_label: 'Topics',
      count: 120,
      color: '#534ad8',
      marker: { radius: 18 },
    },
    {
      id: 'f-delay',
      name: 'Flight-Delay-Compensation-Denied',
      type: 'free_form' as const,
      field: 'Topics' as FreeformFieldKey,
      field_label: 'Topics',
      count: 42,
      cluster: 'Travel Disruption',
      color: '#94a3b8',
      marker: { radius: 10 },
    },
    {
      id: 'f-baggage',
      name: 'Baggage-Delayed-On-International-Itinerary',
      type: 'free_form' as const,
      field: 'Topics' as FreeformFieldKey,
      field_label: 'Topics',
      count: 28,
      cluster: 'Travel Disruption',
      color: '#94a3b8',
      marker: { radius: 9 },
    },
    {
      id: 'c-loyalty',
      name: 'Loyalty Program',
      type: 'cluster' as const,
      field: 'Topics' as FreeformFieldKey,
      field_label: 'Topics',
      count: 88,
      color: '#534ad8',
      marker: { radius: 15 },
    },
    {
      id: 'f-aeroplan',
      name: 'Aeroplan-Points-Missing-After-Redemption',
      type: 'free_form' as const,
      field: 'Topics' as FreeformFieldKey,
      field_label: 'Topics',
      count: 31,
      cluster: 'Loyalty Program',
      color: '#94a3b8',
      marker: { radius: 10 },
    },
  ];
  const links = [
    { from: 'c-travel', to: 'f-delay', weight: 42, field: 'Topics' as FreeformFieldKey },
    { from: 'c-travel', to: 'f-baggage', weight: 28, field: 'Topics' as FreeformFieldKey },
    { from: 'c-loyalty', to: 'f-aeroplan', weight: 31, field: 'Topics' as FreeformFieldKey },
  ];
  return {
    start_date: startDate,
    end_date: endDate,
    timeZone,
    fields: {},
    graph: { nodes, links },
    new_cluster_candidates: [
      {
        field: 'Topics',
        field_label: 'Topics',
        current_cluster: 'Others',
        free_form_value: 'Strike-Anxiety-Travel-Impact',
        count: 14,
        share_of_cluster: 0.22,
        lexical_overlap: 0.18,
        reasons: ['Others overflow', 'Weak alignment'],
        suggested_cluster_name: 'Labor Disruption',
        priority_score: 0.81,
      },
      {
        field: 'RootCause',
        field_label: 'Root Cause',
        current_cluster: 'System / Tech',
        free_form_value: 'App-Push-Triggered-False-Cancel',
        count: 9,
        share_of_cluster: 0.15,
        lexical_overlap: 0.25,
        reasons: ['Cohesive group'],
        suggested_cluster_name: 'Digital Notification Error',
        priority_score: 0.74,
      },
    ],
    suggested_cluster_groups: [
      {
        field: 'Topics',
        field_label: 'Topics',
        suggested_cluster_name: 'Labor Disruption',
        anchor_token: 'strike',
        members: [
          {
            free_form_value: 'Strike-Anxiety-Travel-Impact',
            count: 14,
            current_cluster: 'Others',
          },
          {
            free_form_value: 'Airport-Labor-Action-Misconnect',
            count: 8,
            current_cluster: 'Others',
          },
        ],
        total_count: 22,
        member_count: 2,
      },
    ],
    summary: {
      cluster_nodes: 2,
      free_form_nodes: 3,
      edges: 3,
      candidates: 2,
    },
  };
}

export function buildDummyClusterReviewItems(): ClusterReviewItem[] {
  return [
    {
      field: 'Topics',
      field_label: 'Topics',
      free_form_value: 'Strike-Anxiety-Travel-Impact',
      count: 14,
      assigned_cluster_name: 'Others',
      best_similarity: 0.48,
      similarity_pct: 48,
      closest_clusters: [
        {
          cluster_id: 101,
          cluster_name: 'Travel Disruption',
          value_text: 'Travel Disruption',
          similarity: 0.48,
          similarity_pct: 48,
          mechanism: 'embedding',
        },
        {
          cluster_id: 102,
          cluster_name: 'Loyalty Program',
          value_text: 'Loyalty Program',
          similarity: 0.31,
          similarity_pct: 31,
          mechanism: 'embedding',
        },
      ],
      recommendation: {
        action: 'human_review',
        similarity_pct: 48,
        label: 'Needs review',
        detail: 'Below auto-assign threshold — confirm cluster or create new.',
        allow_create_cluster: true,
        allow_mark_other: true,
        allow_reassign: true,
      },
    },
    {
      field: 'RootCause',
      field_label: 'Root Cause',
      free_form_value: 'App-Push-Triggered-False-Cancel',
      count: 9,
      assigned_cluster_name: 'System / Tech',
      best_similarity: 0.62,
      similarity_pct: 62,
      closest_clusters: [
        {
          cluster_id: 201,
          cluster_name: 'System / Tech',
          value_text: 'System / Tech',
          similarity: 0.62,
          similarity_pct: 62,
          mechanism: 'embedding',
        },
      ],
      recommendation: {
        action: 'auto_assign',
        similarity_pct: 62,
        label: 'Likely System / Tech',
        detail: 'Similarity above review threshold.',
        allow_create_cluster: false,
        allow_mark_other: true,
        allow_reassign: true,
      },
    },
  ];
}

export function buildDummyReviewConversations(freeFormValue: string): any[] {
  return Array.from({ length: 5 }).map((_, i) => ({
    ConversationId: `CONV-REV-${i + 1}`,
    ContactId: `CONTACT-REV-${i + 1}`,
    InitiationTimestamp: dayjs().subtract(i + 1, 'day').toISOString(),
    Summary: `Dummy HITL row for ${freeFormValue}`,
    PrimaryTopic: 'Refunds',
    Intent: 'Request-Refund',
    Sentiment: 'Negative',
    JourneyMoment: 'Post-Travel',
    CallerType: 'Leisure',
    Resolution: 'Partially Resolved',
    CustomerSentimentAtEnd: 'Negative',
    CallResolution: 'Partially Resolved',
  }));
}
